import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's featured media (carousel) or
   * replacing the gallery with server-rendered HTML (grid, or when select not possible).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const mediaCol = this.closest('.product-information__media');
    const lpoWrapperPresent = Boolean(mediaCol?.querySelector('.cl-po--gallery-wrapper'));
    if (lpoWrapperPresent) return;

    // When the LPO (Live Product Options) app is configured to mount onto this gallery,
    // variant updates can fire before LPO injects its wrapper. In that window, replacing
    // <media-gallery> would wipe out the soon-to-mount LPO gallery (race condition).
    const lpoSelector = window.Cloudlift?.options?.config?.autoGallerySelector;
    const lpoOwnsThisGallery = Boolean(
      lpoSelector && (this.matches(lpoSelector) || this.closest(lpoSelector))
    );
    if (lpoOwnsThisGallery) return;

    const variant = event.detail.resource;
    const featuredMediaId = variant?.featured_media?.id;

    // Carousel: select the variant's featured media slide instead of full replace (avoids image breakage)
    if (
      this.presentation === 'carousel' &&
      featuredMediaId != null &&
      this.slideshow?.refs?.slides?.length
    ) {
      const slideIndex = this.slideshow.refs.slides.findIndex(
        (s) => s.getAttribute('slide-id') === String(featuredMediaId)
      );
      if (slideIndex !== -1) {
        this.slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
        return;
      }
    }

    // Grid or no matching slide: replace with server HTML
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // If LPO is on the page, give it a brief chance to mount before we replace.
    // This avoids a race where variant:update arrives first, then LPO mounts, but
    // the theme has already replaced <media-gallery> and destroyed LPO's target.
    const shouldDeferForLpoMount = Boolean(window.Cloudlift) && !lpoWrapperPresent;
    if (shouldDeferForLpoMount && mediaCol && mediaCol.dataset.ftLpoVariantDefer !== '1') {
      mediaCol.dataset.ftLpoVariantDefer = '1';
      window.setTimeout(() => {
        mediaCol.dataset.ftLpoVariantDefer = '';
        if (!this.isConnected) return;
        if (mediaCol.querySelector('.cl-po--gallery-wrapper')) return;
        if (lpoSelector && (this.matches(lpoSelector) || this.closest(lpoSelector))) return;
        this.replaceWith(newMediaGallery.cloneNode(true));
      }, 75);
      return;
    }

    // Clone before inserting to avoid cross-document node adoption issues.
    this.replaceWith(newMediaGallery.cloneNode(true));
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
